import { Outlet, createFileRoute, Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard, FileText, Database, ImageIcon, Mail, Bot,
  LogOut, ExternalLink, Settings, Users as UsersIcon, User, ChevronDown,
  ArrowRightLeft, Activity, Inbox, Send, Webhook, Tags, Search, MessageCircle,
  Palette, Blocks, LayoutTemplate,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api, unwrap } from '@/lib/api';
import { config } from '@/lib/config';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu';
import { CommandPalette } from '@/components/CommandPalette';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useCommandPalette } from '@/lib/command-palette';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app')({
  component: AppLayout,
});

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeKey?: 'pages' | 'posts' | 'media' | 'forms' | 'leads' | 'users' | 'messages' | 'newsletter';
  match?: string;
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/', label: 'Escritorio', icon: LayoutDashboard },
  { to: '/pages', label: 'Páginas', icon: FileText, badgeKey: 'pages' },
  { to: '/post-types', label: 'Contenidos', icon: Database, badgeKey: 'posts' },
  { to: '/taxonomies', label: 'Taxonomías', icon: Tags },
  { to: '/media', label: 'Multimedia', icon: ImageIcon, badgeKey: 'media' },
  { to: '/forms', label: 'Formularios', icon: Mail, badgeKey: 'forms' },
  { to: '/messages', label: 'Mensajes', icon: Inbox, badgeKey: 'messages' },
  { to: '/newsletter', label: 'Newsletter', icon: Send, badgeKey: 'newsletter' },
  { to: '/chatbot', label: 'Chatbot', icon: MessageCircle },
  { to: '/ai/leads', label: 'IA · Leads', icon: Bot, badgeKey: 'leads' },
];

// Tema, patrones y plantillas solo tienen sentido cuando el frontend consume
// los tokens del CMS. Las webs con diseño propio lo apagan por instancia con
// CMS_FEATURE_DESIGN=false.
const DESIGN_NAV: NavItem[] = [
  { to: '/theme', label: 'Tema', icon: Palette },
  { to: '/patterns', label: 'Patrones', icon: Blocks },
  { to: '/page-templates', label: 'Plantillas', icon: LayoutTemplate },
];

const SECONDARY_NAV: NavItem[] = [
  { to: '/integrations', label: 'Integraciones', icon: Webhook },
  { to: '/redirects', label: 'Redirecciones', icon: ArrowRightLeft },
  { to: '/activity', label: 'Actividad', icon: Activity },
  { to: '/users', label: 'Usuarios', icon: UsersIcon, badgeKey: 'users' },
  { to: '/settings', label: 'Ajustes', icon: Settings },
];

interface DashStats {
  pages: { total: number; published: number; drafts: number };
  posts: { total: number; published: number; drafts: number };
  media: { total: number };
  forms: { total: number; submissions_total: number; submissions_24h: number };
  leads: { total: number; last_24h: number };
  users: { total: number };
}

function AppLayout() {
  const { user, logout } = useAuth();
  const router = useRouterState();
  const pathname = router.location.pathname;
  const siteUrl = config.frontendUrl;
  const openPalette = useCommandPalette((s) => s.setOpen);

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats-sidebar'],
    queryFn: () => unwrap<DashStats>(api.get('/dashboard/stats')),
    refetchInterval: 60_000,
  });

  const { data: version } = useQuery({
    queryKey: ['cms-version'],
    queryFn: () => unwrap<{ name: string; version: string }>(api.get('/version')),
    staleTime: Infinity,
  });

  const initials = (user?.name ?? user?.email ?? 'SA')
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  function badgeValue(key?: NavItem['badgeKey']): number | null {
    if (!key || !stats) return null;
    if (key === 'pages') return stats.pages.total;
    if (key === 'posts') return stats.posts.total;
    if (key === 'media') return stats.media.total;
    if (key === 'forms') return stats.forms.submissions_24h || stats.forms.total;
    if (key === 'leads') return stats.leads.last_24h || stats.leads.total;
    if (key === 'users') return stats.users.total;
    return null;
  }

  return (
    <div className="grid min-h-screen grid-rows-[3.25rem_1fr] grid-cols-[16rem_1fr]">
      {/* TOPBAR */}
      <header
        className="col-span-2 flex items-center justify-between border-b px-4 text-white"
        style={{
          background: 'linear-gradient(90deg, #080510 0%, #1A0E2B 55%, #2B0E3E 100%)',
          borderBottomColor: 'rgba(173,2,106,0.45)',
        }}
      >
        <div className="flex items-center">
          {config.brandLogoUrl ? (
            <img src={config.brandLogoUrl} alt={config.brandName} className="h-9 w-auto" />
          ) : (
            <span className="text-sm font-semibold tracking-tight">{config.brandName}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openPalette(true)}
            className="inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-white/70 transition-colors hover:text-white"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
            title="Buscar y ejecutar acciones (⌘K)"
          >
            <Search className="size-3.5" />
            <span className="hidden sm:inline">Buscar</span>
            <kbd className="hidden rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium sm:inline">⌘K</kbd>
          </button>
          <ThemeToggle />
          <a
            href={siteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 px-3 py-1.5 text-xs hover:bg-neutral-800"
          >
            <ExternalLink className="size-3.5" /> Ver sitio
          </a>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-neutral-800">
                <Avatar className="size-7">
                  <AvatarFallback className="bg-[#AD026A] text-white text-[11px]">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-xs sm:inline">{user?.name}</span>
                <ChevronDown className="size-3.5 text-neutral-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">{user?.name}</span>
                  <span className="truncate text-xs font-normal text-neutral-500">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex w-full items-center gap-2">
                  <User className="size-4" /> Mi perfil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex w-full items-center gap-2">
                  <Settings className="size-4" /> Ajustes del sitio
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => void logout()} className="text-red-600 focus:text-red-700">
                <LogOut className="size-4" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* SIDEBAR */}
      <aside className="row-start-2 flex flex-col border-r border-neutral-200 bg-white">
        <nav className="flex-1 space-y-6 overflow-y-auto p-3">
          <NavGroup label="Contenido">
            {PRIMARY_NAV.map((item) => (
              <NavLink key={item.to} item={item} badge={badgeValue(item.badgeKey)} pathname={pathname} />
            ))}
          </NavGroup>
          {config.features.design && (
            <NavGroup label="Diseño">
              {DESIGN_NAV.map((item) => (
                <NavLink key={item.to} item={item} badge={null} pathname={pathname} />
              ))}
            </NavGroup>
          )}
          <NavGroup label="Administración">
            {SECONDARY_NAV.map((item) => (
              <NavLink key={item.to} item={item} badge={badgeValue(item.badgeKey)} pathname={pathname} />
            ))}
          </NavGroup>
        </nav>
        <div className="border-t border-neutral-200 p-3">
          <Button variant="ghost" className="w-full justify-start" onClick={() => void logout()}>
            <LogOut className="size-4" /> Cerrar sesión
          </Button>
          <p className="mt-2 text-center text-[10px] text-neutral-400">
            {version?.name ?? 'CMS'} · v{version?.version ?? '—'}
          </p>
        </div>
      </aside>

      {/* MAIN */}
      <main className="row-start-2 overflow-auto bg-neutral-50">
        <Outlet />
      </main>

      <CommandPalette />
    </div>
  );
}

function NavGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
        {label}
      </h3>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function NavLink({ item, badge, pathname }: { item: NavItem; badge: number | null; pathname: string }) {
  const Icon = item.icon;
  const active = item.to === '/'
    ? pathname === '/'
    : pathname === item.to || pathname.startsWith(item.to + '/');
  return (
    <li>
      <Link
        to={item.to}
        className={cn(
          'group flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm transition-colors',
          active
            ? 'text-white'
            : 'text-neutral-700 hover:bg-neutral-100',
        )}
        style={active ? { background: 'linear-gradient(90deg, #66023C 0%, #AD026A 100%)' } : undefined}
      >
        <span className="flex items-center gap-3">
          <Icon className="size-4" />
          {item.label}
        </span>
        {badge != null && badge > 0 && (
          <Badge
            variant={active ? 'default' : 'secondary'}
            className={cn(
              'h-5 min-w-[1.25rem] justify-center px-1.5 text-[10px]',
              active && 'bg-white/15 text-white',
            )}
          >
            {badge > 99 ? '99+' : badge}
          </Badge>
        )}
      </Link>
    </li>
  );
}
